import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  Badge,
  UserBadge,
  BadgeSummary,
  BadgeProgress,
  UpdateBadgeDisplayRequest,
} from 'types/interfaces/badge';

export const badgeApi = createApi({
  reducerPath: 'badgeApi',
  baseQuery: baseQuery,
  tagTypes: ['UserBadges', 'BadgeSummary', 'BadgeCatalog'],
  endpoints: (builder) => ({
    // GET /badges — all badge definitions grouped by category (public)
    getAllBadges: builder.query<Record<string, Badge[]>, void>({
      query: () => ({
        url: '/badges',
        method: 'GET',
        requiresAuth: false,
      }),
      transformResponse: (response: any): Record<string, Badge[]> => {
        if (!response) return {};
        try {
          if (Array.isArray(response)) {
            const map: Record<string, Badge[]> = {};
            response.forEach((group: any) => {
              if (group.category && Array.isArray(group.badges)) {
                map[group.category] = group.badges;
              }
            });
            return map;
          }
          if (typeof response === 'object') return response;
          return {};
        } catch {
          return {};
        }
      },
      providesTags: ['BadgeCatalog'],
    }),

    // GET /badges/users/:userId — all earned badges for a user (public)
    getUserBadges: builder.query<UserBadge[], number>({
      query: (userId) => ({
        url: `/badges/users/${userId}`,
        method: 'GET',
        requiresAuth: false,
      }),
      transformResponse: (response: any): UserBadge[] => {
        if (Array.isArray(response)) return response;
        if (response?.earned && Array.isArray(response.earned)) return response.earned;
        if (response?.badges && Array.isArray(response.badges)) return response.badges;
        if (response?.data && Array.isArray(response.data)) return response.data;
        return [];
      },
      providesTags: (_result, _error, userId) => [
        { type: 'UserBadges', id: userId },
      ],
    }),

    // GET /badges/users/:userId/summary — displayed badges + maxBadgeSlots (public)
    getBadgeSummary: builder.query<BadgeSummary, number>({
      query: (userId) => ({
        url: `/badges/users/${userId}/summary`,
        method: 'GET',
        requiresAuth: false,
      }),
      transformResponse: (response: any): BadgeSummary => {
        // Backend may return a flat array of displayed UserBadges, or the full object
        if (Array.isArray(response)) {
          return { displayedBadges: response, maxBadgeSlots: 4 };
        }
        return {
          displayedBadges: response?.displayedBadges ?? response?.displayed ?? [],
          maxBadgeSlots: response?.maxBadgeSlots ?? 4,
        };
      },
      providesTags: (_result, _error, userId) => [
        { type: 'BadgeSummary', id: userId },
      ],
    }),

    // GET /badges/users/:userId/progress — badge progress stats (auth required, own profile only)
    getBadgeProgress: builder.query<BadgeProgress, number>({
      query: (userId) => ({
        url: `/badges/users/${userId}/progress`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_result, _error, userId) => [
        { type: 'UserBadges', id: userId },
      ],
    }),

    // PUT /badges/display — set displayed badges (auth required)
    updateBadgeDisplay: builder.mutation<void, UpdateBadgeDisplayRequest>({
      query: (body) => ({
        url: '/badges/display',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserBadges', 'BadgeSummary'],
    }),
  }),
});

export const {
  useGetAllBadgesQuery,
  useGetUserBadgesQuery,
  useGetBadgeSummaryQuery,
  useGetBadgeProgressQuery,
  useUpdateBadgeDisplayMutation,
} = badgeApi;
